import { ServiceType } from '../../enums/contractor';
import type { ProfileInlineEditSection } from './ProfileInlineUxContext';

/** True if react-hook-form `dirtyFields` subtree has any dirty leaf. */
export function dirtySubtree(value: unknown): boolean {
    if (value === true) return true;
    if (!value || typeof value !== 'object') return false;
    return Object.values(value as Record<string, unknown>).some(dirtySubtree);
}

export function companyNestedSectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return dirtySubtree(dirtyFields.company);
}

const PERSONAL_SHELL_DIRTY_KEYS = [
    'firstName',
    'lastName',
    'isActive',
    'phone',
    'vehiclePlate',
    'department',
] as const;

export function personalShellSectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return PERSONAL_SHELL_DIRTY_KEYS.some((key) =>
        dirtySubtree(dirtyFields[key as string]),
    );
}

export function loginShellSectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return dirtySubtree(dirtyFields.email);
}

const CONTRACTOR_ORG_COMPANY_KEYS = [
    'name',
    'primaryContact',
    'abn',
    'email',
    'phone',
    'streetAddress',
    'suburb',
    'state',
    'countryCode',
    'postcode',
] as const;

const CONTRACTOR_SERVICES_KEYS = ['services', 'serviceStates'] as const;

const CONTRACTOR_TERMS_KEYS = ['cancellationPolicy'] as const;

const CONTRACTOR_AVAILABILITY_KEYS = [
    'availableDays',
    'unavailablePeriods',
] as const;

const CONTRACTOR_COMPANY_ROOT_KEYS = [
    ...CONTRACTOR_ORG_COMPANY_KEYS,
    ...CONTRACTOR_SERVICES_KEYS,
    ...CONTRACTOR_TERMS_KEYS,
    ...CONTRACTOR_AVAILABILITY_KEYS,
] as const;

export function contractorOrgCompanySectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return CONTRACTOR_ORG_COMPANY_KEYS.some((key) =>
        dirtySubtree(dirtyFields[key as string]),
    );
}

export function contractorServicesSectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return CONTRACTOR_SERVICES_KEYS.some((key) =>
        dirtySubtree(dirtyFields[key as string]),
    );
}

export function contractorTermsSectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return CONTRACTOR_TERMS_KEYS.some((key) =>
        dirtySubtree(dirtyFields[key as string]),
    );
}

export function contractorAvailabilitySectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return CONTRACTOR_AVAILABILITY_KEYS.some((key) =>
        dirtySubtree(dirtyFields[key as string]),
    );
}

/** Any contractor organisation field dirty (legacy / combined checks). */
export function contractorCompanySectionDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return CONTRACTOR_COMPANY_ROOT_KEYS.some((key) =>
        dirtySubtree(dirtyFields[key as string]),
    );
}

/** True if the profile picture field is dirty (e.g. new upload not yet saved). */
export function profilePictureIsDirty(
    dirtyFields: Record<string, unknown> | undefined,
): boolean {
    if (!dirtyFields) return false;
    return dirtySubtree(dirtyFields.profilePicture);
}

/** True if the given inline section has unsaved field changes in `dirtyFields`. */
export function profileInlineSectionHasDirtyChanges(
    section: ProfileInlineEditSection,
    dirtyFields: Record<string, unknown> | undefined,
    contractorCompany: boolean,
): boolean {
    if (!section) return false;
    if (section === 'company') {
        return companyNestedSectionDirty(dirtyFields);
    }
    if (contractorCompany) {
        if (section === 'contractor_company') {
            return contractorOrgCompanySectionDirty(dirtyFields);
        }
        if (section === 'contractor_services') {
            return contractorServicesSectionDirty(dirtyFields);
        }
        if (section === 'contractor_terms') {
            return contractorTermsSectionDirty(dirtyFields);
        }
        if (section === 'contractor_availability') {
            return contractorAvailabilitySectionDirty(dirtyFields);
        }
    }
    if (section === 'personal') {
        return personalShellSectionDirty(dirtyFields);
    }
    if (section === 'login') {
        return loginShellSectionDirty(dirtyFields);
    }
    return false;
}

/** True if saving would drop a non-empty services list because the user changed `services.type` since inline edit opened. */
export function contractorSaveShouldWarnServiceTypeChange(
    snapshot: Record<string, unknown> | null,
    currentServices: unknown,
): boolean {
    const prev = snapshot?.services as
        | { type?: string; items?: unknown }
        | undefined;
    const cur = currentServices as { type?: string } | undefined;
    if (!prev?.type || !cur?.type) return false;
    if (prev.type === cur.type) return false;
    const items = prev.items;
    return Array.isArray(items) && items.length > 0;
}

/** Item keys allowed for each `services.type` (kept in sync with contractor profile schema). */
const CONTRACTOR_SERVICE_ITEM_KEYS_BY_TYPE: Record<
    string,
    ReadonlySet<string>
> = {
    cleaning: new Set([
        'regularClean',
        'deepClean',
        'changeLinen',
        'outdoorCleaning',
        'bbqCleaning',
        'windowCleaning',
        'carpetCleaning',
        'linenWashing',
    ]),
    catering: new Set([
        'customMenu',
        'setMenu',
        'foodStation',
        'dietaryRequests',
        'alcoholService',
        'onsiteChef',
        'equipment',
        'delivery',
        'cleanup',
    ]),
    alcohol: new Set([
        'fullBar',
        'signatureCocktails',
        'bartenderService',
        'alcoholCatering',
        'wineAndBeerTasting',
        'nonAlcoholicOptions',
        'cocktailStations',
        'iceServices',
        'alocholDelivery',
        'mobileAndOutdoorBars',
        'postEventCleanUp',
    ]),
    workshop: new Set([
        'candleMaking',
        'pottery',
        'artClass',
        'cookingClass',
        'cocktailClass',
    ]),
    wellness: new Set([
        'massage',
        'pilates',
        'yoga',
        'meditation',
        'soundHealing',
    ]),
    other: new Set([]),
};

const ALL_PREDEFINED_SERVICE_ITEM_KEYS = new Set<string>(
    Object.values(CONTRACTOR_SERVICE_ITEM_KEYS_BY_TYPE).flatMap((keys) =>
        Array.from(keys),
    ),
);

/** Drops any service line items that do not belong to the selected service type before save. */
export function filterContractorServiceItemsForSelectedType(
    type: string | undefined,
    items: string[] | undefined,
): string[] {
    if (!type) return [];
    if (!Array.isArray(items)) return [];

    if (type === 'other') {
        return items.filter((item) => item.trim().length > 0);
    }

    const allowed = CONTRACTOR_SERVICE_ITEM_KEYS_BY_TYPE[type];
    if (!allowed) return [];

    return items.filter((item) => {
        if (item.trim().length === 0) {
            return false;
        }
        return allowed.has(item) || !ALL_PREDEFINED_SERVICE_ITEM_KEYS.has(item);
    });
}
